import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { WebhookEventsCell } from 'src/components/webhook/grid/WebhookEventsCell'
import { WebhookStatusCell } from 'src/components/webhook/grid/WebhookStatusCell'
import { WebhookSubscriptionActionsCell } from 'src/components/webhook/grid/WebhookSubscriptionActionsCell'
import { WebhookSubscriptionIdCell } from 'src/components/webhook/grid/WebhookSubscriptionIdCell'
import type { WebhookSubscription } from 'src/interfaces/webhook'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { webhookCreationSourceLabels, webhookSubscriptionsTableHeaderTitles } from './constants'
import { WebhookSubscriptionsTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, cells from components/webhook/grid.
 *
 * A factory rather than a module constant: the actions column closes over the page's handlers.
 * Callers must memoize the result, since a fresh array on every render would reset the grid's
 * column state.
 */
export const buildWebhookSubscriptionColumns = (
  onEditSubscription: (subscription: WebhookSubscription) => void,
  onToggleSubscription: (subscription: WebhookSubscription) => void,
  isSaving: boolean,
  // `webhook_subscriptions_manage` (ENG-1207 part 2). Edit and Enable/Disable both need it, so
  // without it the actions column is dropped.
  canManage: boolean,
): Array<GridColDef<WebhookSubscription>> => {
  const columns: Array<GridColDef<WebhookSubscription>> = [
    {
      field: WebhookSubscriptionsTableFields.callbackUrl,
      headerName:
        webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.callbackUrl],
      flex: 3,
      minWidth: 240,
      sortable: true,
      // The callback URL identifies the row, so it is never offered for hiding.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.callbackUrl}
          </Typography>
        )
      },
    },
    {
      field: WebhookSubscriptionsTableFields.events,
      headerName: webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.events],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return <WebhookEventsCell events={params.row.events} />
      },
    },
    {
      field: WebhookSubscriptionsTableFields.isActive,
      headerName: webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.isActive],
      width: 120,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return <WebhookStatusCell isActive={params.row.isActive} />
      },
    },
    {
      field: WebhookSubscriptionsTableFields.creationSource,
      headerName:
        webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.creationSource],
      width: 140,
      sortable: true,
      display: 'flex',
      // Read-only. This column is why the tab is a list rather than a single card: a customer
      // holding the `customer.webhook` OAuth scope creates its own subscriptions, which arrive
      // as `api`, and a single-subscription UI would hide them. The backend applies no source
      // check on PUT, so an `api` row is still editable here.
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return (
          <Typography variant="body2">
            {webhookCreationSourceLabels[params.row.creationSource] ?? params.row.creationSource}
          </Typography>
        )
      },
    },
    {
      field: WebhookSubscriptionsTableFields.createdAt,
      headerName: webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      // ENG-1221: the edit modal (which also shows the ID) needs `webhook_subscriptions_manage`,
      // so the list is where read-only users find it. Hideable, unlike the callback URL.
      field: WebhookSubscriptionsTableFields.id,
      headerName: webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.id],
      width: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return <WebhookSubscriptionIdCell subscriptionId={params.row.id} />
      },
    },
    {
      field: WebhookSubscriptionsTableFields.actions,
      headerName: webhookSubscriptionsTableHeaderTitles[WebhookSubscriptionsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookSubscription>) => {
        return (
          <WebhookSubscriptionActionsCell
            subscription={params.row}
            isSaving={isSaving}
            onEdit={onEditSubscription}
            onToggle={onToggleSubscription}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== WebhookSubscriptionsTableFields.actions
  })
}
